<template>
  <div>
    <span
      ref="sanitized"
      style="display:none"
    />
    <Modal
      :value="visible"
      :closable="false"
      :mask-closable="false"
      :draggable="draggable"
      :scrollable="scrollable"
      class="file-tree-selector-modal"
      @on-visible-change="onModalVisibleChange"
    >
      <p slot="header">
        <i
          v-if="fileTreeSelectorModalData.icon"
          :class="fileTreeSelectorModalData.icon"
          style="margin-right: 8px"
        />
        <span>{{ fileTreeSelectorModalData.title }}</span>
      </p>
      <Tree
        :data="fileTreeSelectorModalData.fileTreeData"
        :render="treeNodeRender"
        style="max-height: 240px; overflow: auto; margin-bottom: 16px"
        @on-select-change="selectedNodeArr => onTreeNodeSelect(selectedNodeArr[0])"
      />
      <Input
        v-model="fileName"
        :placeholder="fileTreeSelectorModalData.inputPlaceholder"
        @on-change="errorMessage = ''"
      />
      <div
        v-if="errorMessage"
        style="color: var(--color-red-alert)"
      >
        {{ errorMessage }}
      </div>
      <p slot="footer">
        <Button
          type="success"
          size="large"
          :disabled="!fileName.length || !selectedNode"
          @click="onFileTreeModalSave"
        >
          Save
        </Button>
        <Button
          type="default"
          size="large"
          @click="onFileTreeModalClose"
        >
          Cancel
        </Button>
      </p>
    </Modal>

    <CustomModal
      v-model="confirmReplaceFile.visible"
      :closable="confirmReplaceFile.closable"
      :mask-closable="confirmReplaceFile.maskClosable"
      :category="confirmReplaceFile.category"
      :title="confirmReplaceFile.title"
      :message="confirmReplaceFile.message"
      :action-buttons="confirmReplaceFile.actionButtons"
      :cancel-button="confirmReplaceFile.cancelButton"
      @closeModal="closeConfirmReplaceQueryModal"
    />
  </div>
</template>

<script>
import CustomModal from './CustomModal'
import { WARNING } from '@/constants/buttons'
import _ from 'lodash'
import { mapState } from 'vuex'

export default {

  components: {
    CustomModal
  },

  model: {
    prop: 'visible',
    event: 'change'
  },

  props: {
    userId: Number,
    queryObj: Object,
    visible: Boolean,
    queryRepoMode: {
      type: Boolean,
      default: false
    },
    folderMode: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      fileTreeData: [],
      fileName: '',
      selectedNode: null,
      errorMessage: '',
      queryToSave: this.queryObj,
      confirmReplaceFile: {
        visible: false,
        queryToReplace: null,
        closable: false,
        maskClosable: false,
        category: 'warning',
        title: 'Replace Query?',
        message: '',
        actionButtons: [{
          label: 'Confirm',
          type: WARNING,
          callback: this.onReplaceQueryConfirm.bind(this)
        }],
        cancelButton: {
          label: 'Cancel'
        }
      }
    }
  },

  computed: {
    folderTreeData () {
      const filterForFolders = node => {
        const { children = [] } = node

        node.children = children.filter(child => child.isFolder)
        node.children.forEach(child => filterForFolders(child))
      }

      const folderTree = this.fileTreeData.map(fileTree => {
        const fileTreeCopy = _.cloneDeep(fileTree)
        filterForFolders(fileTreeCopy)

        return fileTreeCopy
      })

      return folderTree
    },

    fileTreeSelectorModalData () {
      if (!this.fileTreeData.length) {
        this.createFileTreeData()
      }

      const modalData = {
        title: this.queryToSave ? `Save Query "${this.queryToSave.name}" As` : '',
        icon: 'far fa-save',
        inputPlaceholder: 'File Name...',
        fileTreeData: this.fileTreeData
      }

      if (this.queryRepoMode) {
        const queryRepo = this.fileTreeData.length > 1 ? this.fileTreeData[1].children : []

        modalData.fileTreeData = queryRepo
        modalData.title = 'Save To Shared Workspace'
        modalData.icon = 'fab fa-google-drive'
        modalData.inputPlaceholder = 'File Name...'
      } else if (this.folderMode) {
        modalData.fileTreeData = this.folderTreeData
        modalData.title = 'Save Folder As'
        modalData.icon = 'fas fa-folder-plus'
        modalData.inputPlaceholder = 'Folder Name...'
      }

      return modalData
    },

    ...mapState('queryRepo', {
      queryRepo: (state, getters) => getters.queryRepo,
      queryRepoFiles: state => state.files,
      queryRepoFolders: state => state.folders
    }),

    ...mapState('queryWorkspace', {
      queryWorkspace: state => state.workspace,
      queryWorkspaceFolders: state => state.folders
    })
  },

  watch: {
    queryWorkspace: {
      handler () {
        this.createFileTreeData()
      },
      deep: true
    },

    queryWorkspaceFolders: {
      handler () {
        this.createFileTreeData()
      },
      deep: true
    },

    queryRepo: {
      handler () {
        this.createFileTreeData()
      },
      deep: true
    },

    queryObj (newQueryObj) {
      this.queryToSave = newQueryObj
      this.fileName = newQueryObj ? newQueryObj.name : ''
    }
  },

  methods: {
    /**
     * Creates the data needed for the File Tree Selector
     */
    createFileTreeData () {
      const fileTree = [
        {
          title: 'My Workspace',
          expand: true,
          isFolder: true,
          isRoot: true,
          children: [],
          filePath: '/'
        },
        {
          title: 'Shared Workspace',
          expand: true,
          children: [],
          isRepo: true,
          isRoot: true,
          isFolder: true,
          insertable: false,
          editable: false,
          filePath: 'shared-workspace/'
        }
      ]

      this.queryWorkspace.forEach(query => {
        const { queryName } = query
        const name = queryName[0] === '/' ? queryName : `/${queryName}`
        const path = name.split('/')

        this.createPathQueryWorkspace(fileTree[0], path, 1)
      })
      this.queryWorkspaceFolders.forEach(folder => {
        const pathStr = folder.path + folder.name
        const path = pathStr.split('/')

        this.createPathQueryWorkspace(fileTree[0], path, 1, true)
      })
      this.createQueryRepoFileTree(fileTree[1])

      // Add shared Folder
      const sharedFolder = {
        title: 'Shared Queries',
        isFolder: true,
        children: [],
        linkedToRepo: true,
        expand: false
      }
      const userFolder = this.getFileTreeUserFolder(fileTree[1])

      if (userFolder) {
        sharedFolder.children = userFolder.children
      }

      fileTree[0].children.push(sharedFolder)
      this.sortQueryWorkspaceFileTree(fileTree[0])
      this.fileTreeData = fileTree
    },

    /**
     * Creates the file path for each node within the workspace.
     * @param {Object} workspace - Object representing the entire Query Pad Workspace
     * @param {Array} path - Array of Strings to represent the current path
     * @param {Number} pathIndex - The current index of the path
     * @param {Boolean} isFolderPath - indicates that the path we want to create is for a folder
     */
    createPathQueryWorkspace (workspace, path, pathIndex, isFolderPath = false) {
      const nodeName = path[pathIndex]

      if (!nodeName || !workspace.children) {
        return
      }

      const isFolder = isFolderPath || pathIndex < path.length - 1
      let treeNode = workspace.children.find(child => child.title === nodeName && Boolean(child.isFolder) === isFolder)

      if (!treeNode) {
        treeNode = {
          title: nodeName,
          isFolder,
          filePath: `${workspace.filePath}${nodeName}`
        }

        if (isFolder) {
          treeNode.children = []
          treeNode.expand = false
          treeNode.filePath += '/'
        }

        workspace.children.push(treeNode)
      }

      this.createPathQueryWorkspace(treeNode, path, pathIndex + 1, isFolderPath)
    },

    /**
     * Sorts the Query Workspace File Tree
     * @param {Object} workspace - File Tree representation of user workspace
     */
    sortQueryWorkspaceFileTree (workspace) {
      if (!workspace.children) {
        return
      }

      workspace.children.sort((a, b) => {
        if (a.linkedToRepo) return -1
        if (b.linkedToRepo) return 1
        if (a.isFolder && !b.isFolder) return -1
        if (!a.isFolder && b.isFolder) return 1
        if (a.title < b.title) return -1
        if (a.title > b.title) return 1

        return 0
      })
      workspace.children.forEach(fileTreeNode => this.sortQueryWorkspaceFileTree(fileTreeNode))
    },

    /**
     * Creates the File Tree for Query Repository
     * @param {Object} sharedWorkspace - Object representing the Query Repository
     */
    createQueryRepoFileTree (sharedWorkspace) {
      if (!this.queryRepo) return

      const fileTreeNodes = []

      this.queryRepoFolders.forEach(folder => {
        const { id, name, userId, insertable, editable, parentId } = folder

        fileTreeNodes.push({
          id,
          title: name,
          isFolder: true,
          userId,
          insertable,
          editable,
          parentId,
          expand: false,
          isRepo: true
        })
      })

      this.queryRepoFiles.forEach(file => {
        const { id, queryName, folderId } = file

        fileTreeNodes.push({
          id,
          title: queryName,
          isFolder: false,
          parentId: folderId,
          isRepo: true
        })
      })

      sharedWorkspace.children = this.createQueryRepoFileTreeHelper(
        fileTreeNodes.filter(node => node.parentId === null),
        fileTreeNodes
      )
    },

    /**
     * Retrieves the "Users" folder from the query repo
     * @param {Object} queryRepo
     * @return {Object}
     */
    getQueryRepoUsersFolder (queryRepo) {
      const { children = [] } = queryRepo

      return children.find(
        child => child.isFolder && !child.editable && !child.insertable && child.title.toLowerCase() === 'users'
      )
    },

    /**
     * Retrieves the current user's folder data within the Query Repository that is to be used for file tree selector.
     * @param {Object} queryRepo - Query Repo object
     * @return {Object | null} - The User Folder or null if it does not exist.
     */
    getFileTreeUserFolder (queryRepo) {
      // Get the Users folder.
      const usersFolder = this.getQueryRepoUsersFolder(queryRepo)

      if (usersFolder) {
        const userEmail = this.$auth.getUser().email.toLowerCase()
        const emailFolder = usersFolder.children.find(child => child.title.toLowerCase() === userEmail)

        if (emailFolder) {
          return emailFolder
        }
      }

      return null
    },

    /**
     * Helper function that recursively builds the tree structure for the query repo file tree selector.
     * @param {Array} parents - Array of the parent nodes for the current level.
     * @param {Array} fileTreeNodes - Array of all nodes within Query Repository.
     * @return {Array} - Array of file nodes that have the appropriate children
     */
    createQueryRepoFileTreeHelper (parents, fileTreeNodes) {
      return parents.map(parent => {
        if (!parent.isFolder) {
          return { ...parent }
        }

        return {
          ...parent,
          children: this.createQueryRepoFileTreeHelper(
            fileTreeNodes.filter(node => node.parentId === parent.id),
            fileTreeNodes
          )
        }
      })
    },

    /**
     * Render function for tree nodes for File Tree Selector
     * @param {Function} h - Render function for Vue/iview
     * @param {Object} data - Data about the tree node
     * @return {Object} - Vue HTML object for rendering on File Tree Selector Modal
     */
    treeNodeRender (h, { data }) {
      let iconClass = ''

      if (data.isRoot && data.isRepo) {
        iconClass = 'fab fa-google-drive'
      } else if (data.linkedToRepo) {
        iconClass = 'fas fa-link'
      } else if (data.isFolder) {
        iconClass = 'fas fa-folder'
      } else {
        iconClass = 'far fa-file'
      }

      let treeNodeClass = ''

      if (data.selected) {
        treeNodeClass += 'tree-node-selected'
      }

      if (!data.isRoot && data.isFolder && !this.folderNodeCanInsert(data)) {
        treeNodeClass += ' tree-node-disabled'
      }

      return h('span', {
        style: {
          display: 'inline-block',
          cursor: 'pointer'
        },
        class: treeNodeClass
      }, [
        h('i', {
          class: iconClass,
          style: 'margin-right: 8px'
        }),
        h('span', data.title)
      ])
    },

    /**
     * Determines if the folder is insertable.
     * @param {Object} folder - Object to represent folder
     * @return {Boolean} true if user can insert into the folder. False if the user cannot
     */
    folderNodeCanInsert (folder) {
      const parentUserFolder = this.getParentUserFolder(folder)

      return (
        !folder.isRepo ||
        (folder.insertable && (!parentUserFolder || (parentUserFolder.userId === this.userId)))
      )
    },

    /**
     * Gets the parent user folder of a folder, if it belongs to one.
     * For example: if folder is found within John Doe's folder, the function
     * will return John Doe's folder (john.doe@). Will return undefined if folder
     * does not belong to a user folder
     * @param {Object} folder
     * @return {Object}
     */
    getParentUserFolder (folder) {
      const usersFolder = this.getQueryRepoUsersFolder(this.fileTreeData[1])

      return usersFolder && usersFolder.children
        .filter(child => child.isFolder)
        .find(userFolder => this.isFolderInFolder(folder, userFolder))
    },

    /**
     * Returns true if targetFolder is a sub folder of the currentFolder.
     * Will search for targetFolder recursively
     * @param {Object} targetFolder
     * @param {Object} currentFolder
     * @return {Boolean}
     */
    isFolderInFolder (targetFolder, currentFolder) {
      if (targetFolder.id === currentFolder.id) {
        return true
      }

      return (currentFolder.children || [])
        .filter(child => child.isFolder)
        .some(folder => this.isFolderInFolder(targetFolder, folder))
    },

    /**
     * Handler for when tree node is selected on File Tree Selector
     * @param {Object} treeNode - tree node object
     */
    onTreeNodeSelect (treeNode) {
      if (treeNode) {
        this.selectedNode = treeNode

        if (!treeNode.isFolder) {
          this.fileName = treeNode.title
        }
      } else {
        this.selectedNode = null
      }
    },

    /**
     * Handler for when Save Button on File Tree Modal is clicked
     */
    async onFileTreeModalSave () {
      if (this.selectedNode.isRepo && this.selectedNode.isFolder && !this.folderNodeCanInsert(this.selectedNode)) {
        this.errorMessage = 'You cannot save a file/folder within the selected folder!'

        return
      }

      if (this.fileName.includes('/')) {
        this.errorMessage = 'You cannot have slashes within your file/folder name!'

        return
      }

      if (this.selectedNode.isRepo && !this.folderMode) {
        let query = null

        if (!this.selectedNode.isFolder) {
          query = this.queryRepoFiles.find(file => file.id === this.selectedNode.id)
        } else {
          query = this.queryRepoFiles.find(file => file.queryName === this.fileName && file.folderId === this.selectedNode.id)
        }

        if (query) {
          this.openConfirmReplaceQueryModal(query)
          return
        }
      }

      this.$emit('onSave', this.fileName, this.selectedNode)
    },

    /**
     * Opens the modal to confirm if user wants to replace query in shared folder
     * @param {Object} query - The query to be replaced
     */
    openConfirmReplaceQueryModal (query) {
      const message = `
        The query with the name <strong>${this.sanitize(query.queryName)}</strong> already exists.
        Would you like to replace it?
      `
      this.$set(this.confirmReplaceFile, 'visible', true)
      this.$set(this.confirmReplaceFile, 'queryToReplace', query)
      this.$set(this.confirmReplaceFile, 'message', message)
    },

    /**
     * Closes the Confirm Replace Query Modal
     */
    closeConfirmReplaceQueryModal () {
      this.$set(this.confirmReplaceFile, 'visible', false)
      this.$set(this.confirmReplaceFile, 'queryToReplace', null)
    },

    /**
     * Called when the user confirms to replace the query
     */
    async onReplaceQueryConfirm () {
      const { queryToReplace } = this.confirmReplaceFile

      this.$emit('onReplace', queryToReplace)
      this.closeConfirmReplaceQueryModal()
      this.onFileTreeModalClose()
    },

    /**
     * Evoked when the file tree modal changes visibility
     * @param {Boolean} visible - True if modal becomes visible. Otherwise false.
     */
    onModalVisibleChange (visible) {
      this.$emit('change', visible)
    },

    /**
     * Evoked when the file tree modal closes
     */
    onFileTreeModalClose () {
      this.$emit('onClose')
    },

    /**
     * Sanitizes a given string to avoid XSS.
     * @param {String} str
     * @return {String}  Sanitized string.
     */
    sanitize (str) {
      this.$refs.sanitized.textContent = str
      return this.$refs.sanitized.innerHTML
    }
  }
}
</script>

<style scoped>
  .file-tree-selector-modal >>> .tree-node-disabled {
    opacity: 0.5;
  }

  .file-tree-selector-modal >>> .tree-node-selected {
    background-color: #d5e8fc;
  }
</style>
